// © 2026 AttainIP Engineering
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, MessageSquare } from 'lucide-react';
import { fetchPublicChats, fetchPublicChat } from '@/api/queries';
import { Card } from '@/components/Card';
import { TranscriptMessage } from '@/components/TranscriptMessage';

function chatTimestamp(c: any): number {
  const t = c.last_message_at || c.updated_at || c.created_at;
  const ms = t ? Date.parse(t) : NaN;
  return Number.isFinite(ms) ? ms : 0;
}

function PublicChatTranscriptDrawer({
  sessionId,
  sessionTitle,
  onClose,
}: {
  sessionId: string;
  sessionTitle: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['public-chat', sessionId],
    queryFn: () => fetchPublicChat(sessionId),
  });
  const messages: any[] = data?.messages || [];

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/30" />
      <div
        className="w-full max-w-3xl h-full bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-4 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold truncate">{sessionTitle}</h2>
            <p className="text-xs text-gray-500 truncate">anonymous · public site</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-2 bg-yellow-50 border-b text-xs text-yellow-800">
          Read-only — anonymous public-site session. Every view is audited.
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading && <p className="text-sm text-gray-400">Loading transcript…</p>}
          {error && <p className="text-sm text-red-600">Failed to load transcript</p>}
          {!isLoading && !error && messages.length === 0 && (
            <p className="text-sm text-gray-400">No messages in this session.</p>
          )}
          <div className="space-y-4">
            {messages.map((m: any, i: number) => (
              <TranscriptMessage key={m.id ?? `${i}-${m.role}`} raw={m} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PublicChatsPage() {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<{ id: string; title: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-chats'],
    queryFn: fetchPublicChats,
  });

  const chats: any[] = useMemo(() => {
    const raw = data?.chats || [];
    return [...raw].sort((a, b) => chatTimestamp(b) - chatTimestamp(a));
  }, [data]);

  const filtered = useMemo(() => {
    if (!search.trim()) return chats;
    const q = search.toLowerCase();
    return chats.filter((c: any) => {
      const id = c.chat_id || c.session_id || c.id || '';
      const title = c.title || c.chat_name || '';
      return (
        String(id).toLowerCase().includes(q) ||
        String(title).toLowerCase().includes(q)
      );
    });
  }, [chats, search]);

  return (
    <div>
      <h1 className="text-xl font-bold mb-2">Public Chats</h1>
      <p className="text-sm text-gray-500 mb-6">
        Assistant sessions from anonymous (logged-out) visitors on the public site.
        All sessions share the same <code>user_identity = "anonymous"</code> in
        Core Services storage — there is no per-visitor partitioning.
      </p>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by session ID or title…"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <Card title={`Sessions (${filtered.length})`}>
        {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
        {error && <p className="text-sm text-red-600">Failed to load public chats</p>}
        {!isLoading && !error && filtered.length === 0 && (
          <p className="text-sm text-gray-400">
            {search ? 'No matching sessions' : 'No public chats yet'}
          </p>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 font-medium">Title</th>
                <th className="pb-2 font-medium">Session ID</th>
                <th className="pb-2 font-medium">Messages</th>
                <th className="pb-2 font-medium">Last activity ↓</th>
                <th className="pb-2 font-medium">Created</th>
                <th className="pb-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c: any) => {
                const id = c.chat_id || c.session_id || c.id;
                const title = c.title || c.chat_name || `Chat ${String(id).slice(0, 8)}…`;
                const created = c.created_at;
                const last = c.last_message_at || c.updated_at;
                const count = c.message_count;
                return (
                  <tr
                    key={id}
                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setOpen({ id, title })}
                  >
                    <td className="py-2 truncate max-w-md">{title}</td>
                    <td className="py-2 font-mono text-xs text-gray-400">
                      {String(id).slice(0, 12)}…
                    </td>
                    <td className="py-2 text-xs text-gray-600">{count ?? '—'}</td>
                    <td className="py-2 text-xs text-gray-400">
                      {last ? new Date(last).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 text-xs text-gray-400">
                      {created ? new Date(created).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2 text-right">
                      <MessageSquare size={14} className="text-gray-400 inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {open && (
        <PublicChatTranscriptDrawer
          sessionId={open.id}
          sessionTitle={open.title}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}
